import moment from 'moment-timezone';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PaymentsTab from '../bookingsDetailsPage/paymentsTab/PaymentsTab';
import Accommodation from './Accommodation';
import CarHires from './CarHires';
import Coaches from './Coaches';
import Cruises from './Cruises';
import Extras from './Extras';
import Flights from './Flights';
import Overview from './Overview';
import Packages from './Packages';
import Passengers from './Passengers';
import Tours from './Tours';
import Transfers from './Transfers';

export default function OverviewTab({
  data = {},
  refreshBookingAfterPayment,
  isBooking = false,
}) {
  const {
    accommodation = [],
    flights = [],
    packages = [],
    tours = [],
    cruises = [],
    coaches = [],
    carHires = [],
    transfers = [],
    extras = [],
  } = data;

  const [sortedComponents, setSortedComponents] = useState([]);
  const { componentConfig } = useSelector((state) => state.ui);

  const extractTimeZone = useCallback((date) => {
    if (typeof date !== 'string') {
      return '';
    }

    const regex = /\[(.*?)\]/;
    const match = date.match(regex);

    if (match) {
      return match[1];
    }

    return '';
  }, []);

  const parseDate = useCallback(
    (date) => {
      if (!date) return null;

      if (typeof date === 'string') {
        return moment.tz(date, 'YYYY-MM-DDTHH:mm:ss[Z]', extractTimeZone(date));
      }

      if (typeof date === 'object' && date.timestamp && date.zone) {
        return moment.tz(date.timestamp, date.zone);
      }

      return null;
    },
    [extractTimeZone],
  );

  const getStartDate = useCallback((item) => item?.startDate || null, []);

  const getEarliestStartDateFromList = useCallback(
    (list) => {
      if (!Array.isArray(list) || list.length === 0) return null;

      let earliest = null;
      for (let i = 0; i < list.length; i += 1) {
        const s = getStartDate(list[i]);
        if (s) {
          if (!earliest) {
            earliest = s;
          } else {
            const parsedCurrent = parseDate(s);
            const parsedEarliest = parseDate(earliest);
            if (parsedCurrent && parsedEarliest && parsedCurrent.isBefore(parsedEarliest)) {
              earliest = s;
            }
          }
        }
      }

      return earliest;
    },
    [getStartDate, parseDate],
  );

  const createComponentList = useCallback(
    () => [
      { component: 'Accommodation', startDate: getStartDate(accommodation[0]) },
      { component: 'Flights', startDate: getStartDate(flights[0]) },
      { component: 'Packages', startDate: getStartDate(packages[0]) },
      { component: 'Tours', startDate: getStartDate(tours[0]) },
      { component: 'Cruises', startDate: getStartDate(cruises[0]) },
      { component: 'Coaches', startDate: getStartDate(coaches[0]) },
      { component: 'CarHires', startDate: getStartDate(carHires[0]) },
      { component: 'Transfers', startDate: getStartDate(transfers[0]) },
    ],
    [
      accommodation,
      flights,
      packages,
      tours,
      cruises,
      coaches,
      carHires,
      transfers,
      getStartDate,
    ],
  );

  const loyaltyExtras = useMemo(() => extras.filter((e) => e && e.loyalty === true), [extras]);
  const nonLoyaltyExtras = useMemo(() => extras.filter((e) => !(e && e.loyalty === true)), [extras]);

  const createExtrasList = useCallback(() => {
    const list = [];

    if (loyaltyExtras.length > 0) {
      list.push({
        component: 'ExtrasLoyalty',
        startDate: getEarliestStartDateFromList(loyaltyExtras),
      });
    }

    if (nonLoyaltyExtras.length > 0) {
      list.push({
        component: 'ExtrasNonLoyalty',
        startDate: getEarliestStartDateFromList(nonLoyaltyExtras),
      });
    }

    return list;
  }, [loyaltyExtras, nonLoyaltyExtras, getEarliestStartDateFromList]);

  const sortByStartDate = useCallback(
    (a, b) => {
      // Always sort Cruises first if present
      if (a.component === 'Cruises' && b.component !== 'Cruises') return -1;
      if (b.component === 'Cruises' && a.component !== 'Cruises') return 1;

      const momentA = parseDate(a.startDate);
      const momentB = parseDate(b.startDate);

      if (!momentA && !momentB) return 0;
      if (!momentA) return 1;
      if (!momentB) return -1;

      return momentA - momentB;
    },
    [parseDate],
  );

  const orderComponentsChronologically = useCallback(() => {
    // Sort core components chronologically, but always append extras groups at end
    const core = [...createComponentList()].sort(sortByStartDate);
    const extrasListOrdered = createExtrasList();

    return core.concat(extrasListOrdered);
  }, [createComponentList, createExtrasList, sortByStartDate]);

  useEffect(() => {
    const sorted = orderComponentsChronologically();

    setSortedComponents((prev) => {
      if (prev !== sorted) {
        return sorted;
      }
      return prev;
    });
  }, [orderComponentsChronologically]);

  return (
    <>
      <Overview data={data} isBooking={isBooking} />
      {componentConfig?.paymentSummaryEnabled && isBooking && (
        <PaymentsTab
          booking={data}
          refreshBookingAfterPayment={refreshBookingAfterPayment}
        />
      )}
      <Passengers data={data} />

      {sortedComponents.map((component) => {
        switch (component.component) {
          case 'Accommodation':
            return (
              accommodation.length > 0 && (
                <Accommodation accommodationList={accommodation} />
              )
            );
          case 'Flights':
            return flights.length > 0 && <Flights flightList={flights} />;
          case 'Packages':
            return packages.length > 0 && <Packages packagesList={packages} />;
          case 'Tours':
            return tours.length > 0 && <Tours toursList={tours} />;
          case 'Cruises':
            return (
              cruises.length > 0 && (
                <Cruises cruisesList={cruises} isBooking={isBooking} />
              )
            );
          case 'Coaches':
            return coaches.length > 0 && <Coaches coachesList={coaches} />;
          case 'CarHires':
            return carHires.length > 0 && <CarHires carHiresList={carHires} />;
          case 'Transfers':
            return (
              transfers.length > 0 && <Transfers transfersList={transfers} />
            );
              case 'ExtrasLoyalty':
                return (
                  loyaltyExtras.length > 0 && (
                    <Extras key="ExtrasLoyalty" extrasList={loyaltyExtras} />
                  )
                );
              case 'ExtrasNonLoyalty':
                return (
                  nonLoyaltyExtras.length > 0 && (
                    <Extras key="ExtrasNonLoyalty" extrasList={nonLoyaltyExtras} />
                  )
                );
          default:
            return null;
        }
      })}
    </>
  );
}
